import { keyframes, css, cx } from 'emotion';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import IconButton from '@hig/icon-button';
import ThemeContext from '@hig/theme-context';
import Typography from '@hig/typography';
import { CloseSUI, CloseMUI } from '@hig/icons';
import { createCustomClassNames, generateId } from '@hig/utils';

var types = Object.freeze({
  STANDARD: "standard",
  ALTERNATE: "alternate"
});

var AVAILABLE_TYPES = Object.freeze(Object.values(types));

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _templateObject = _taggedTemplateLiteral(["\n  0% { top: 131px; }\n  30% { top: 125px; }\n"], ["\n  0% { top: 131px; }\n  30% { top: 125px; }\n"]);

function _taggedTemplateLiteral(strings, raw) { return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }

var MODAL_BODY_LAYER = 1001;
var MODAL_HEADER_LAYER = 1002;
var MODAL_LAYER = 10001;

var WindowClosing = keyframes(_templateObject);

function stylesheet(props, themeData) {
  var open = props.open,
      type = props.type;


  var headerFontSize = themeData["density.fontSizes.large"];
  var headerLinHeight = 1.66666;

  return {
    modal: {
      wrapper: {
        color: themeData["modal.fontColor"],
        opacity: open ? 1.0 : 0,
        pointerEvents: open ? "visible" : "none",
        transition: "all ease 0.2s",
        transitionDelay: open ? "0s" : "0.2s"
      },

      overlay: {
        backgroundColor: themeData["modal.overlayColor"],
        bottom: 0,
        left: 0,
        position: "fixed",
        right: 0,
        top: 0,
        zIndex: MODAL_LAYER
      },

      window: {
        background: themeData["modal.shell.backgroundColor"],
        borderRadius: themeData["modal.shell.borderRadius"],
        boxShadow: "0 0 8px 0 " + themeData["modal.shell.shadowColor"],
        display: "flex",
        flexDirection: "column",
        left: "50%",
        height: themeData["modal.shell.minHeight"],
        width: themeData["modal.shell.minWidth"],
        opacity: open ? 1.0 : 0,
        outline: "none",
        position: "fixed",
        top: "128px",
        transform: "translateX(-50%)",
        zIndex: MODAL_BODY_LAYER,

        animation: open ? "none" : WindowClosing + " 0.4s",
        transition: "all ease 0.2s",
        transitionDelay: "0.2s"
      },

      header: _extends({
        fontSize: headerFontSize,
        fontWeight: themeData["basics.fontWeights.medium"],
        lineHeight: headerLinHeight,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        borderBottom: "1px solid " + themeData["modal.header.borderBottomColor"],
        minHeight: themeData["modal.header.minHeight"],
        zIndex: MODAL_HEADER_LAYER

      }, type === types.ALTERNATE && {
        backgroundColor: themeData["modal.header.backgroundColor"]
      }),

      headerContent: _extends({
        margin: "0 " + themeData["modal.paddingHorizontal"],
        display: "flex",
        justifyContent: "space-between",
        "svg *": {
          fill: themeData["modal.fontColor"]
        },
        "&:hover, &:focus": {
          "svg *": {
            fill: themeData["modal.fontColor"]
          }
        }
      }, type === types.ALTERNATE && {
        backgroundColor: themeData["modal.header.backgroundColor"]
      }),

      body: {
        display: "flex",
        flex: "1 1 auto",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        minHeight: themeData["modal.body.minHeight"]
      },

      bodyContent: {
        border: "none",
        flex: "1 1 auto",
        overflowX: "auto",
        overflowY: "auto",
        padding: themeData["modal.paddingHorizontal"]
      }
    }
  };
}

var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var ModalHeaderPresenter = function (_Component) {
  _inherits(ModalHeaderPresenter, _Component);

  function ModalHeaderPresenter() {
    _classCallCheck(this, ModalHeaderPresenter);

    return _possibleConstructorReturn(this, (ModalHeaderPresenter.__proto__ || Object.getPrototypeOf(ModalHeaderPresenter)).apply(this, arguments));
  }

  _createClass(ModalHeaderPresenter, [{
    key: "renderChildren",
    value: function renderChildren() {
      var _props = this.props,
          children = _props.children,
          id = _props.id,
          styles = _props.styles;

      return React.createElement(
        "header",
        { className: css(styles), id: id },
        children
      );
    }
  }, {
    key: "render",
    value: function render() {
      var _props2 = this.props,
          children = _props2.children,
          closeButtonAriaLabel = _props2.closeButtonAriaLabel,
          id = _props2.id,
          onCloseClick = _props2.onCloseClick,
          title = _props2.title,
          styles = _props2.styles,
          otherProps = _objectWithoutProperties(_props2, ["children", "closeButtonAriaLabel", "id", "onCloseClick", "title", "styles"]);

      var className = otherProps.className;


      return children ? this.renderChildren() : React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref) {
          var metadata = _ref.metadata;

          var closeIcon = metadata.densityId === "medium-density" ? React.createElement(CloseMUI, null) : React.createElement(CloseSUI, null);
          var headerClassName = createCustomClassNames(className, "modal-header");
          var headerContentClassName = createCustomClassNames(className, "modal-header-content");
          return React.createElement(
            "header",
            { className: cx(css(styles.header), headerClassName), id: id },
            React.createElement(
              "div",
              {
                className: cx(css(styles.headerContent), headerContentClassName)
              },
              React.createElement(
                Typography,
                {
                  style: {
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    lineHeight: "inherit"
                  }
                },
                title
              ),
              React.createElement(IconButton, {
                "aria-label": closeButtonAriaLabel,
                icon: closeIcon,
                onClick: onCloseClick,
                title: closeButtonAriaLabel
              })
            )
          );
        }
      );
    }
  }]);

  return ModalHeaderPresenter;
}(Component);

ModalHeaderPresenter.propTypes = {
  /**
   * Supports adding any dom content to the header of the modal
   */
  children: PropTypes.node,
  /**
   * ARIA label attribute for the close button if/when children
   * are not utilized
   */
  closeButtonAriaLabel: PropTypes.string,
  /**
   * ID for a11y
   */
  id: PropTypes.string,
  /**
   * Triggers when one clicks the close button
   */
  onCloseClick: PropTypes.func,
  /**
   * Styles for the modal header
   */
  styles: PropTypes.objectOf(PropTypes.any),
  /**
   * Title of the modal
   */
  title: PropTypes.node
};
ModalHeaderPresenter.defaultProps = {
  closeButtonAriaLabel: "Close"
};
ModalHeaderPresenter.__docgenInfo = {
  "description": "",
  "displayName": "ModalHeaderPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Supports adding any dom content to the header of the modal"
    },
    "closeButtonAriaLabel": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "ARIA label attribute for the close button if/when children\nare not utilized",
      "defaultValue": {
        "value": "\"Close\"",
        "computed": false
      }
    },
    "id": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "ID for a11y"
    },
    "onCloseClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when one clicks the close button"
    },
    "styles": {
      "type": {
        "name": "objectOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": "Styles for the modal header"
    },
    "title": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Title of the modal"
    }
  }
};

var _createClass$1 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$1(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var ModalPresenter = function (_Component) {
  _inherits$1(ModalPresenter, _Component);

  function ModalPresenter() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck$1(this, ModalPresenter);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn$1(this, (_ref = ModalPresenter.__proto__ || Object.getPrototypeOf(ModalPresenter)).call.apply(_ref, [this].concat(args))), _this), _this.titleId = generateId("modal-title"), _temp), _possibleConstructorReturn$1(_this, _ret);
  }

  _createClass$1(ModalPresenter, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _props = this.props,
          children = _props.children,
          closeButtonAriaLabel = _props.closeButtonAriaLabel,
          headerChildren = _props.headerChildren,
          onCloseClick = _props.onCloseClick,
          onOverlayClick = _props.onOverlayClick,
          onWindowClick = _props.onWindowClick,
          open = _props.open,
          customizeStyles = _props.stylesheet,
          title = _props.title,
          type = _props.type,
          otherProps = _objectWithoutProperties$1(_props, ["children", "closeButtonAriaLabel", "headerChildren", "onCloseClick", "onOverlayClick", "onWindowClick", "open", "stylesheet", "title", "type"]);

      var className = otherProps.className;

      var modalOverlayClassName = createCustomClassNames(className, "modal-overlay");
      var modalWindowClassName = createCustomClassNames(className, "modal-window");
      var modalBodyClassName = createCustomClassNames(className, "modal-body");
      var modalBodyContentClassName = createCustomClassNames(className, "modal-body-content");

      /*
       * The "no-noninteractive-element-interactions" rule is disabled for this block.
       * This is due to the modal being is a special case where its containers are to be considered
       * as non-interactive, static content by screen-readers, but must also respond to `click`
       * events. Additionally, even though they respond to `click` events, they're not focusable.
       */
      /*
        eslint-disable
        jsx-a11y/no-noninteractive-element-interactions,
        jsx-a11y/click-events-have-key-events
      */

      return React.createElement(
        ThemeContext.Consumer,
        null,
        function (_ref2) {
          var resolvedRoles = _ref2.resolvedRoles,
              metadata = _ref2.metadata;

          var styles = stylesheet({ open: open, type: type }, resolvedRoles);

          var cssStyles = customizeStyles ? customizeStyles(styles, _this2.props, resolvedRoles, metadata.colorSchemeId) : styles;

          return React.createElement(
            "div",
            { className: cx(css(cssStyles.modal.wrapper), className) },
            React.createElement(
              "div",
              {
                "aria-labelledby": _this2.titleId,
                className: cx(css(cssStyles.modal.overlay), modalOverlayClassName),
                onClick: onOverlayClick,
                role: "dialog",
                tabIndex: "-1"
              },
              React.createElement(
                "article",
                {
                  className: cx(css(cssStyles.modal.window), modalWindowClassName),
                  onClick: onWindowClick,
                  role: "document"
                },
                React.createElement(
                  ModalHeaderPresenter,
                  {
                    className: className,
                    id: _this2.titleId,
                    closeButtonAriaLabel: closeButtonAriaLabel,
                    onCloseClick: onCloseClick,
                    styles: cssStyles.modal,
                    title: title
                  },
                  headerChildren
                ),
                React.createElement(
                  "section",
                  {
                    className: cx(css(cssStyles.modal.body), modalBodyClassName)
                  },
                  React.createElement(
                    "div",
                    {
                      className: cx(css(cssStyles.modal.bodyContent), modalBodyContentClassName)
                    },
                    children
                  )
                )
              )
            )
          );
        }
      );
      /*
        eslint-enable
        jsx-a11y/no-noninteractive-element-interactions,
        jsx-a11y/click-events-have-key-events
      */
    }
  }]);

  return ModalPresenter;
}(Component);

ModalPresenter.propTypes = {
  /**
   * Supports adding any dom content to the body of the modal
   */
  children: PropTypes.node,
  /**
   * ARIA label attribute for the close button if/when headerChildren
   * are not utilized, also used for the title attribute
   */
  closeButtonAriaLabel: PropTypes.string,
  /**
   * Supports adding any dom content to the header of the modal
   */
  headerChildren: PropTypes.node,
  /**
   * Triggers when one clicks the close button
   */
  onCloseClick: PropTypes.func,
  /**
   * Triggers when one clicks the overlay behind the modal
   */
  onOverlayClick: PropTypes.func,
  /**
   * Triggers when one clicks the modal window
   */
  onWindowClick: PropTypes.func,
  /**
   * Modal is visible when true
   */
  open: PropTypes.bool,
  /**
   * Enables modification of Modal Styles
   */
  stylesheet: PropTypes.func,
  /**
   * Title of the modal
   */
  title: PropTypes.node,
  /**
   * Style of the modal shell
   */
  type: PropTypes.string
};
ModalPresenter.defaultProps = {
  type: types.STANDARD
};
ModalPresenter.__docgenInfo = {
  "description": "",
  "displayName": "ModalPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Supports adding any dom content to the body of the modal"
    },
    "closeButtonAriaLabel": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "ARIA label attribute for the close button if/when headerChildren\nare not utilized, also used for the title attribute"
    },
    "headerChildren": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Supports adding any dom content to the header of the modal"
    },
    "onCloseClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when one clicks the close button"
    },
    "onOverlayClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when one clicks the overlay behind the modal"
    },
    "onWindowClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when one clicks the modal window"
    },
    "open": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Modal is visible when true"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Enables modification of Modal Styles"
    },
    "title": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Title of the modal"
    },
    "type": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Style of the modal shell",
      "defaultValue": {
        "value": "types.STANDARD",
        "computed": true
      }
    }
  }
};

var _createClass$2 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$2(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$2(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var ModalBehavior = function (_Component) {
  _inherits$2(ModalBehavior, _Component);

  function ModalBehavior() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck$2(this, ModalBehavior);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn$2(this, (_ref = ModalBehavior.__proto__ || Object.getPrototypeOf(ModalBehavior)).call.apply(_ref, [this].concat(args))), _this), _this.handleCloseClick = function (event) {
      var onCloseClick = _this.props.onCloseClick;


      if (onCloseClick) {
        onCloseClick(event);
      }
    }, _this.handleOverlayClick = function (event) {
      var onOverlayClick = _this.props.onOverlayClick;


      if (onOverlayClick) {
        onOverlayClick(event);
      }
    }, _this.handleWindowClick = function (event) {
      event.stopPropagation();
    }, _temp), _possibleConstructorReturn$2(_this, _ret);
  }

  /**
   * @param {MouseEvent} event
   */


  /**
   * @param {MouseEvent} event
   */


  /**
   * @param {MouseEvent} event
   */


  _createClass$2(ModalBehavior, [{
    key: "render",
    value: function render() {
      return this.props.children({
        handleCloseClick: this.handleCloseClick,
        handleOverlayClick: this.handleOverlayClick,
        handleWindowClick: this.handleWindowClick
      });
    }
  }]);

  return ModalBehavior;
}(Component);

ModalBehavior.propTypes = {
  /**
   * Triggers when you click the close button
   */
  onCloseClick: PropTypes.func,
  /**
   * Triggers when you click the overlay behind the modal
   */
  onOverlayClick: PropTypes.func,
  /**
   * A function to render content of the modal
   */
  children: PropTypes.func
};
ModalBehavior.__docgenInfo = {
  "description": "",
  "displayName": "ModalBehavior",
  "props": {
    "onCloseClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when you click the close button"
    },
    "onOverlayClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when you click the overlay behind the modal"
    },
    "children": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A function to render content of the modal"
    }
  }
};

var _createClass$3 = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

function _objectWithoutProperties$2(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function _classCallCheck$3(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn$3(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits$3(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

var Modal = function (_Component) {
  _inherits$3(Modal, _Component);

  function Modal() {
    _classCallCheck$3(this, Modal);

    return _possibleConstructorReturn$3(this, (Modal.__proto__ || Object.getPrototypeOf(Modal)).apply(this, arguments));
  }

  _createClass$3(Modal, [{
    key: "render",
    value: function render() {
      var _props = this.props,
          children = _props.children,
          closeButtonAriaLabel = _props.closeButtonAriaLabel,
          headerChildren = _props.headerChildren,
          onCloseClick = _props.onCloseClick,
          onOverlayClick = _props.onOverlayClick,
          open = _props.open,
          stylesheet = _props.stylesheet,
          title = _props.title,
          type = _props.type,
          otherProps = _objectWithoutProperties$2(_props, ["children", "closeButtonAriaLabel", "headerChildren", "onCloseClick", "onOverlayClick", "open", "stylesheet", "title", "type"]);

      var className = otherProps.className;


      return React.createElement(
        ModalBehavior,
        {
          onCloseClick: onCloseClick,
          onOverlayClick: onOverlayClick,
          open: open
        },
        function (_ref) {
          var handleCloseClick = _ref.handleCloseClick,
              handleOverlayClick = _ref.handleOverlayClick,
              handleWindowClick = _ref.handleWindowClick;
          return React.createElement(
            ModalPresenter,
            {
              className: className,
              closeButtonAriaLabel: closeButtonAriaLabel,
              headerChildren: headerChildren,
              onCloseClick: handleCloseClick,
              onOverlayClick: handleOverlayClick,
              onWindowClick: handleWindowClick,
              open: open,
              stylesheet: stylesheet,
              title: title,
              type: type
            },
            children
          );
        }
      );
    }
  }]);

  return Modal;
}(Component);

Modal.propTypes = {
  /**
   * Supports adding any dom content to the body of the modal
   */
  children: PropTypes.node,
  /**
   * ARIA label attribute for the close button if/when headerChildren
   * are not utilized, also used for the title attribute
   */
  closeButtonAriaLabel: PropTypes.string,
  /**
   * Supports adding any dom content to the header of the modal
   */
  headerChildren: PropTypes.node,
  /**
   * Triggers when you click the close button
   */
  onCloseClick: PropTypes.func,
  /**
   * Triggers when you click the overlay behind the modal
   */
  onOverlayClick: PropTypes.func,
  /**
   * Modal is visible when true
   */
  open: PropTypes.bool,
  /**
   * Enables modification of Modal Styles
   */
  stylesheet: PropTypes.func,
  /**
   * Title of the modal
   */
  title: PropTypes.node,
  /**
   * Style of the modal shell
   */
  type: PropTypes.string
};
Modal.defaultProps = {
  closeButtonAriaLabel: "Close"
};
Modal.__docgenInfo = {
  "description": "",
  "displayName": "Modal",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Supports adding any dom content to the body of the modal"
    },
    "closeButtonAriaLabel": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "ARIA label attribute for the close button if/when headerChildren\nare not utilized, also used for the title attribute",
      "defaultValue": {
        "value": "\"Close\"",
        "computed": false
      }
    },
    "headerChildren": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Supports adding any dom content to the header of the modal"
    },
    "onCloseClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when you click the close button"
    },
    "onOverlayClick": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when you click the overlay behind the modal"
    },
    "open": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Modal is visible when true"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Enables modification of Modal Styles"
    },
    "title": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Title of the modal"
    },
    "type": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "Style of the modal shell"
    }
  }
};

export default Modal;
